// src/main/shell/symbolGrid/pagePrefetch.ts
//
// Optimistic first-page prefetch for the symbol tree. When the user HOVERS a
// controller in the nav (≥ a short dwell), we fire the indexer's first-window
// query early and cache the result, so that when they click in, the grid's
// first paint is instant — the page is already here (or already in flight).
//
// Two wins layered:
//   1. Server-side warm-up — the indexer runs the query + caches it (singleflight
//      + per-controller result cache), so even a client-cache MISS clicks fast.
//   2. Client-side cache — an exact-match first window (offset 0, expand-all, no
//      filter) is consumed straight from here for a zero-RTT first paint.
//
// Bounded: a small LRU-ish cap + a short TTL keep this to a handful of ~30 KB
// pages at most, transient — it never grows with catalog size or browse history.

import type { BackendClient } from '../../bridge/backend-client';
import type { SymbolPageData } from '../../../core/bridge/connect-client';

const TTL_MS = 8000; // a warmed page older than this is re-fetched on click anyway
const MAX_ENTRIES = 12; // cap resident prefetched pages (evict oldest on overflow)
const WIN = 1200; // must match IndexerPagedView's WIN (the first-window size)

interface Entry {
  page: Promise<SymbolPageData | undefined>;
  ts: number;
}
const cache = new Map<string, Entry>();

/** One cache, two scopes: a controller's symbol catalog and an integration's
 *  schema share the exact SymbolQuery→SymbolPage contract, so the entries are
 *  interchangeable — only the fetch RPC differs. Keys carry the scope kind so a
 *  controller and an integration with the same id can never collide. */
function warm(key: string, fetch: () => Promise<SymbolPageData>): void {
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && now - hit.ts < TTL_MS) return; // already warm
  const page = fetch().catch(() => undefined); // best-effort; never surfaces an error
  cache.set(key, { page, ts: now });
  // Evict the oldest entry if we're over the cap (Map preserves insertion order).
  if (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
}

async function take(key: string): Promise<SymbolPageData | undefined> {
  const hit = cache.get(key);
  cache.delete(key); // one-shot regardless
  if (!hit || Date.now() - hit.ts > TTL_MS) return undefined;
  return hit.page;
}

/**
 * Warm the indexer + cache the first window for `controllerId`. Idempotent
 * within the TTL (a repeated hover is a no-op). Safe to call on every dwell.
 */
export function prefetchFirstPage(gatewayId: string, controllerId: string, backend: BackendClient): void {
  // The default first window the IndexerPagedView fetches on a fresh visit.
  warm(`${gatewayId}:${controllerId}`, () =>
    backend.api.fetchSymbolPage(gatewayId, controllerId, { offset: 0, pageSize: WIN, expandedPaths: [] }),
  );
}

/**
 * Consume the prefetched first page if it's still fresh, else `undefined`.
 * One-shot — the caller (the view's first ensureStream) takes ownership.
 */
export function takeFirstPage(gatewayId: string, controllerId: string): Promise<SymbolPageData | undefined> {
  return take(`${gatewayId}:${controllerId}`);
}

/** The integration twin (IntegrationIndex, same wire contract) — hovering an
 *  integration card warms its schema's first window, so clicking in paints
 *  instantly, exactly like the controller cards. */
export function prefetchIntegrationFirstPage(gatewayId: string, integrationId: string, backend: BackendClient): void {
  warm(`${gatewayId}:int:${integrationId}`, () =>
    backend.api.fetchIntegrationPage(gatewayId, integrationId, { offset: 0, pageSize: WIN, expandedPaths: [] }),
  );
}

export function takeIntegrationFirstPage(gatewayId: string, integrationId: string): Promise<SymbolPageData | undefined> {
  return take(`${gatewayId}:int:${integrationId}`);
}
